import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import TestimonialSlider from '../components/TestimonialSlider';
import FAQAccordion from '../components/FAQAccordion';
import ContactForm from '../components/ContactForm';
import { fetchCollection, fetchSingleItem, normalizeSlug } from '../lib/cms';
import { useLogoTheme } from '../context/LogoThemeContext';
import { imageUrl } from '../lib/images';

export default function Home() {
  const { headerBgColor } = useLogoTheme();
  const [testimonials, setTestimonials] = useState([]);
  const [faqItems, setFaqItems] = useState([]);
  const [forlobList, setForlobList] = useState([]);
  const [forsideData, setForsideData] = useState(null);
  const [kontaktData, setKontaktData] = useState(null);

  useEffect(() => {
    async function loadData() {
      const [testimonialsData, faqData, forlobData, forsideRes, kontaktRes] = await Promise.all([
        fetchCollection('anmeldelser'),
        fetchCollection('faq'),
        fetchCollection('forlob'),
        fetchSingleItem('forside'),
        fetchSingleItem('kontakt_oplysninger')
      ]);
      if (testimonialsData && testimonialsData.length > 0) setTestimonials(testimonialsData);
      if (faqData && faqData.length > 0) setFaqItems(faqData);
      if (forlobData && forlobData.length > 0) {
        const sorted = [...forlobData].sort((a, b) => (Number(a.raekkefolge) || 99) - (Number(b.raekkefolge) || 99));
        setForlobList(sorted);
      }
      if (forsideRes) setForsideData(forsideRes);
      if (kontaktRes) setKontaktData(kontaktRes);
    }
    loadData();
  }, []);

  // CMS data or fallback defaults
  const heroBadge = forsideData?.hero_badge || 'Velkommen til Lenea Evolinee';
  const heroOverskrift = forsideData?.hero_overskrift || 'Hvor hjertet taler,';
  const heroUnderstregning = forsideData?.hero_understregning || 'og dit hjerte bliver hørt';
  const heroBilledeRaw = forsideData?.hero_billede?.[0] || '/_assets/lenea-evolinee-portrait.webp';
  const heroBillede = imageUrl(heroBilledeRaw, 'width=1000,quality=85,format=auto');
  const heroCirkelTitel = forsideData?.hero_cirkel_badge_titel || 'Fra hjertet';
  const heroCirkelSub = forsideData?.hero_cirkel_badge_sub || '100% Helhed';
  const heroCtaTekst = forsideData?.hero_cta_tekst || 'Book en samtale';
  const heroCtaSekundaer = forsideData?.hero_cta_sekundaer_tekst || 'Se forløb';

  const skabtBadge = forsideData?.skabt_badge || 'Hvorfor Lenea Evolinee blev skabt';
  const skabtOverskrift = forsideData?.skabt_overskrift || 'Fra fragmentering til helhed';
  const skabtTekst1 = forsideData?.skabt_tekst_1 || 'Lenea Evolinee blev skabt af Lenea ud fra en erkendelse af, at det moderne samfund i høj grad fremmer adskillelse. Mange kvinder oplever at leve i deres hoveder, overhøre kropslige advarselssignaler og konstant sætte andres behov før deres egne.';
  const skabtTekst2 = forsideData?.skabt_tekst_2 || 'Efter mange års arbejde med kropsterapeutiske teknikker, energiarbejde og astrologisk analyse blev det tydeligt, at den største og mest varige forvandling sker, når disse metoder flettes sammen i én samlet proces.';
  const skabtBilledeRaw = forsideData?.skabt_billede?.[0] || 'https://assets.auxodev.dk/assets/40e16184-d312-45e0-99c1-a83e20a53a01/img-5755.jpeg';
  const skabtBillede = imageUrl(skabtBilledeRaw, 'width=1000,quality=80,format=auto');

  const fremhaevetBadge = forsideData?.fremhaevet_forlob_badge || 'Gruppeforløb for kvinder';
  const fremhaevetTitel = forsideData?.fremhaevet_forlob_titel || 'FORBUNDET FRA HJERTET';
  const fremhaevetUndertitel = forsideData?.fremhaevet_forlob_undertitel || 'Et trygt og kærligt rum for kvinder';
  const fremhaevetDetaljer1 = forsideData?.fremhaevet_forlob_detaljer_1 || '5 mødegange · hver anden uge · 2,5 timer';
  const fremhaevetDetaljer2 = forsideData?.fremhaevet_forlob_detaljer_2 || '6 kvinder · 3.500 kr.';
  const fremhaevetCta = forsideData?.fremhaevet_forlob_cta || 'Læs mere om Forbundet fra Hjertet →';

  // Kontaktoplysninger
  const adresse = kontaktData?.adresse ? `${kontaktData.adresse}, ${kontaktData.postnr_by || '3520 Farum'}` : 'Ganløsevej 38, 3520 Farum';
  const telefon = kontaktData?.telefon || '24 91 43 13';
  const telefonClean = telefon.replace(/\s+/g, '');
  const email = kontaktData?.email || 'kontakt@leneaevolinee.dk';
  const cvr = kontaktData?.cvr || '31679478';

  return (
    <>
      <SEO
        title="Lenea Evolinee - Et nyt kapitel fra hjertet"
        description="Hvor hjertet taler, og dit hjerte bliver hørt. Helhedsorienteret kropsterapi, Reiki Healing, astrologi og intime forløb for kvinder hos Lenea Evolinee i Farum."
      />

      {/* Hero Section */}
      <section
        style={{ backgroundColor: headerBgColor }}
        className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden transition-colors duration-300"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-12 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-6 text-left">
              <div>
                <span className="text-xs sm:text-sm uppercase tracking-widest text-[#98866C] font-semibold block mb-1">
                  {heroBadge}
                </span>
              </div>

              <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-normal text-[#2E2A27] leading-[1.15] tracking-tight">
                {heroOverskrift} <br className="hidden sm:inline" />
                <span className="italic font-light text-[#98866C]">{heroUnderstregning}</span>
              </h1>

              <div className="pt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#98866C] text-white font-medium text-xs uppercase tracking-widest shadow-xs hover:bg-[#83735C] transition-all duration-200 cursor-pointer"
                >
                  <span>{heroCtaTekst}</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
                <Link
                  to="/forlob"
                  className="inline-flex items-center justify-center px-8 py-4 bg-[#FAF7F2] text-[#2E2A27] border border-[#D8CDBF] font-medium text-xs uppercase tracking-widest hover:bg-[#EAE3D5] transition-all duration-200 cursor-pointer"
                >
                  {heroCtaSekundaer}
                </Link>
              </div>
            </div>

            {/* Right Large Image with Arch Crop */}
            <div className="lg:col-span-5 relative">
              <div className="relative rounded-arch overflow-hidden shadow-xl border border-[#D8CDBF] aspect-4/5 max-w-md mx-auto lg:max-w-none bg-[#EAE3D5]">
                <img
                  src={heroBillede}
                  alt="Lenea Evolinee"
                  className="w-full h-full object-cover object-center"
                />
              </div>

              {/* Decorative Circular Badge Overlay */}
              <div className="absolute bottom-4 left-6 sm:bottom-8 sm:left-10 bg-[#98866C] text-white p-4 sm:p-5 rounded-full shadow-lg flex flex-col items-center justify-center w-28 h-28 text-center border-2 border-[#FAF7F2]">
                <i className="fa-sharp fa-solid fa-sparkles text-xs text-white mb-1" aria-hidden="true" />
                <span className="text-[9px] uppercase tracking-widest font-semibold text-white">{heroCirkelTitel}</span>
                <span className="font-serif text-xs font-medium">{heroCirkelSub}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Metrics & Stats Bar */}
      <section className="bg-[#98866C] text-white py-10 border-y border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-0 lg:divide-x divide-white/20">
            <div className="flex flex-col items-center text-center justify-center p-3 sm:p-4">
              <i className="fa-sharp fa-solid fa-sparkles text-2xl sm:text-3xl text-white mb-2 sm:mb-3" aria-hidden="true" />
              <span className="font-serif text-3xl sm:text-4xl font-normal text-white">100%</span>
              <span className="text-[11px] uppercase tracking-widest text-white/90 mt-1 font-semibold">
                Fra hjertet
              </span>
            </div>

            <div className="flex flex-col items-center text-center justify-center p-3 sm:p-4">
              <i className="fa-solid fa-hands-holding-heart text-2xl sm:text-3xl text-white mb-2 sm:mb-3" aria-hidden="true" />
              <span className="font-serif text-3xl sm:text-4xl font-normal text-white">Helhed</span>
              <span className="text-[11px] uppercase tracking-widest text-white/90 mt-1 font-semibold">
                Krop, sind & energi
              </span>
            </div>

            <div className="flex flex-col items-center text-center justify-center p-3 sm:p-4">
              <i className="fa-solid fa-users text-2xl sm:text-3xl text-white mb-2 sm:mb-3" aria-hidden="true" />
              <span className="font-serif text-2xl sm:text-3xl font-normal text-white">1:1 & Gruppe</span>
              <span className="text-[11px] uppercase tracking-widest text-white/90 mt-1 font-semibold">
                Nærværende Forløb
              </span>
            </div>

            <div className="flex flex-col items-center text-center justify-center p-3 sm:p-4">
              <i className="fa-solid fa-location-dot text-2xl sm:text-3xl text-white mb-2 sm:mb-3" aria-hidden="true" />
              <span className="font-serif text-2xl sm:text-3xl font-normal text-white">Farum</span>
              <span className="text-[11px] uppercase tracking-widest text-white/90 mt-1 font-semibold">
                Roligt & Trygt Fristed
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Hvorfor Lenea Evolinee blev skabt - Fra fragmentering til helhed */}
      <section className="py-20 lg:py-24 bg-white border-b border-[#E8DFD5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 space-y-6">
              <span className="text-xs uppercase tracking-widest text-[#98866C] font-semibold block">
                {skabtBadge}
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
                {skabtOverskrift}
              </h2>
              <p className="text-base text-[#5C524A] leading-relaxed font-sans whitespace-pre-line">
                {skabtTekst1}
              </p>
              <p className="text-base text-[#5C524A] leading-relaxed font-sans whitespace-pre-line">
                {skabtTekst2}
              </p>
            </div>

            <div className="lg:col-span-6">
              <div className="rounded-arch overflow-hidden border border-[#D8CDBF] shadow-lg aspect-4/3 bg-[#EAE3D5]">
                <img
                  src={skabtBillede}
                  alt="Lenea Evolinee"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. FORSIDEN – KORT INTRODUKTION TIL “FORBUNDET FRA HJERTET” */}
      <section className="py-24 bg-[#FAF7F2] border-b border-[#E8DFD5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 sm:p-14 rounded-sm border border-[#D8CDBF] shadow-sm space-y-8">
            <div className="space-y-3">
              <span className="text-xs uppercase tracking-widest text-[#98866C] font-semibold block">
                {fremhaevetBadge}
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
                {fremhaevetTitel}
              </h2>
              <p className="text-lg text-[#98866C] font-medium font-serif italic">
                {fremhaevetUndertitel}
              </p>
              <div className="w-16 h-0.5 bg-[#98866C]/60 pt-1" />
            </div>

            <div className="space-y-5 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
              <p>
                Måske mærker du en længsel efter noget andet.
              </p>
              <p>
                Måske er du træt af at tilpasse dig, være den dygtige og have styr på det hele. Måske mærker du uro i kroppen eller sindet og længes efter mere ro, nærvær og forbindelse til dig selv.
              </p>
              <p>
                Forbundet fra Hjertet er et intimt gruppeforløb for kvinder, hvor du får mulighed for at sænke skuldrene, lytte indad og mærke, hvad der virkelig føles sandt for dig.
              </p>
              <p>
                Her skal du ikke præstere, fikse eller være noget bestemt.
              </p>
              <p className="font-medium text-[#2E2A27]">
                Du bliver mødt, præcis som du er – med nærvær, omsorg og kærlighed.
              </p>
              <p>
                Sammen skaber vi et trygt og fortroligt rum, hvor vi kan dele, spejle os i hinanden og give plads til det, der ønsker at forandre sig.
              </p>
              <p>
                En invitation til at slippe det, der ikke længere føles som dig – og langsomt finde hjem til dig selv.
              </p>
              <p className="font-serif text-xl text-[#2E2A27] font-medium pt-2">
                Måske er det nu, du skal vælge dig selv.
              </p>

              {/* Detaljer */}
              <div className="p-6 bg-[#FAF7F2] rounded-sm border border-[#E8DFD5] space-y-2 mt-6">
                <p className="text-base sm:text-lg font-medium text-[#2E2A27]">
                  {fremhaevetDetaljer1}
                </p>
                <p className="text-base sm:text-lg font-serif text-[#98866C] font-semibold">
                  {fremhaevetDetaljer2}
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="pt-4">
              <Link
                to="/forbundet-fra-hjertet"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#98866C] text-white font-medium text-xs uppercase tracking-widest hover:bg-[#83735C] transition-colors cursor-pointer shadow-xs"
              >
                <span>{fremhaevetCta}</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Forløb Oversigt */}
      <section className="py-24 bg-white border-b border-[#E8DFD5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-16 gap-6">
            <div>
              <span className="text-xs uppercase tracking-widest text-[#98866C] font-semibold block">Forløb & Fællesskaber</span>
              <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
                Trygge rum skabt til din rejse hjem
              </h2>
            </div>
            <Link
              to="/forlob"
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#98866C] hover:text-[#83735C] transition-colors cursor-pointer"
            >
              <span>Se alle forløb</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          <div className={`grid grid-cols-1 sm:grid-cols-2 ${(forlobList.length > 0 ? forlobList.length : 3) >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-6 lg:gap-8`}>
            {(forlobList.length > 0 ? forlobList : [
              {
                id: 'forbundet',
                forlob_slug: 'forbundet-fra-hjertet',
                badge: '5 mødegange · 6 kvinder',
                titel: 'Forbundet fra Hjertet',
                kort_beskrivelse: 'Et intimt gruppeforløb for kvinder, hvor du får mulighed for at sænke skuldrene, lytte indad og mærke, hvad der virkelig føles sandt for dig.',
                billede: ['/cdn-cgi/image/width=600,quality=80,format=auto/assets/40e16184-d312-45e0-99c1-a83e20a53a01/forlob-group.png']
              },
              {
                id: '1-1',
                forlob_slug: '1-1-forlob',
                badge: 'Personligt 1:1-forløb',
                titel: 'En rejse hjem til dig selv',
                kort_beskrivelse: 'Et personligt 1:1-forløb for kvinder. Vi skaber et trygt og fortroligt rum, der tager udgangspunkt i dig, dine behov og dine grænser.',
                billede: ['/_assets/lenea-evolinee-session.webp']
              },
              {
                id: 'lyt',
                forlob_slug: 'lyt-fra-hjertet',
                badge: 'Månedligt fællesskab · 200 kr.',
                titel: 'Lyt – fra Hjertet',
                kort_beskrivelse: 'Et trygt rum, hvor du må dele og bare blive hørt. Vi mødes én gang om måneden i nærvær, fortrolighed og uden gode råd.',
                billede: ['/assets/40e16184-d312-45e0-99c1-a83e20a53a01/img-5756.jpeg']
              }
            ]).map((card, idx) => {
              const cardImgRaw = Array.isArray(card.billede) ? card.billede[0] : card.billede;
              const cardImg = cardImgRaw ? imageUrl(cardImgRaw, 'width=600,quality=80,format=auto') : null;
              const norm = normalizeSlug(card.forlob_slug);
              let linkTo = '/forlob';
              if (norm === 'forbundet-fra-hjertet') linkTo = '/forbundet-fra-hjertet';
              else if (norm === '1-1-forlob' || norm === '1-1-forloeb') linkTo = '/1-1-forlob';
              else if (norm === 'lyt-fra-hjertet') linkTo = '/lyt-fra-hjertet';
              else if (norm === 'forbundet-med-dit-horoskop' || norm === 'astrologisk-1-1-session' || norm === 'astrologi-fra-hjertet') linkTo = '/forbundet-med-dit-horoskop';
              else if (norm) linkTo = `/${norm}`;

              const cardDesc =
                card.kort_beskrivelse ||
                card.beskrivelse ||
                (card.lang_beskrivelse
                  ? card.lang_beskrivelse.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 180) + '...'
                  : '');

              return (
                <div key={card.id || idx} className="bg-[#FAF7F2] rounded-sm border border-[#D8CDBF] overflow-hidden flex flex-col justify-between p-6 sm:p-7 hover:border-[#98866C] transition-all duration-300 shadow-xs">
                  <div className="space-y-4">
                    {cardImg ? (
                      <div className="aspect-16/10 rounded-arch overflow-hidden mb-6 border border-[#D8CDBF] bg-[#EAE3D5]">
                        <img
                          src={cardImg}
                          alt={card.titel}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="aspect-16/10 rounded-arch overflow-hidden mb-6 border border-[#D8CDBF] bg-[#EAE3D5] flex items-center justify-center text-[#98866C]">
                        <i className="fa-solid fa-sparkles text-3xl" aria-hidden="true" />
                      </div>
                    )}
                    {card.badge && (
                      <span className="text-[10px] sm:text-[11px] font-semibold text-[#98866C] tracking-widest uppercase block leading-relaxed">
                        {card.badge}
                      </span>
                    )}
                    <h3 className="font-serif text-2xl font-normal text-[#2E2A27]">
                      {card.titel}
                    </h3>
                    {cardDesc && (
                      <p className="text-sm text-[#5C524A] leading-relaxed line-clamp-4">
                        {cardDesc}
                      </p>
                    )}
                  </div>

                  <div className="pt-6 mt-6 border-t border-[#E8DFD5]">
                    <Link
                      to={linkTo}
                      className="inline-flex items-center justify-between w-full text-xs font-semibold uppercase tracking-widest text-[#98866C] hover:text-[#83735C] transition-colors cursor-pointer"
                    >
                      <span>{card.cta_tekst || 'Læs mere'}</span>
                      <div className="w-8 h-8 rounded-full border border-[#98866C] flex items-center justify-center text-xs">
                        <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                      </div>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Klientudtalelser Slider */}
      <section className="py-24 bg-white border-b border-[#E8DFD5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
            <span className="text-xs uppercase tracking-widest text-[#98866C] font-semibold block">Klienternes oplevelse</span>
            <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
              Det siger kvinderne
            </h2>
          </div>

          <TestimonialSlider testimonials={testimonials} />
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-24 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
            <span className="text-xs uppercase tracking-widest text-[#98866C] font-semibold block">
              Spørgsmål & Svar
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
              Ofte stillede spørgsmål
            </h2>
          </div>

          <FAQAccordion items={faqItems} />
        </div>
      </section>

      {/* Dark CTA Banner + Contact Section */}
      <section className="py-24 bg-white border-t border-[#E8DFD5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div id="kontakt-formular" className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 space-y-6">
              <span className="text-xs uppercase tracking-widest text-[#98866C] font-semibold block">
                Tag første skridt
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
                Book en uforpligtende samtale
              </h2>
              <p className="text-base text-[#5C524A] leading-relaxed font-sans">
                {kontaktData?.kontakt_intro || 'Er du i tvivl om, hvilket forløb eller behandling der passer bedst til din nuværende situation? Udfyld formularen eller kontakt mig direkte – jeg glæder mig til at tale med dig.'}
              </p>

              <div className="space-y-4 pt-4 border-t border-[#E8DFD5] text-sm text-[#2E2A27]">
                <p className="flex items-center gap-3">
                  <i className="fa-solid fa-location-dot text-[#D8ADA0]" aria-hidden="true" />
                  <span>{adresse}</span>
                </p>
                <p className="flex items-center gap-3">
                  <i className="fa-solid fa-phone text-[#D8ADA0]" aria-hidden="true" />
                  <a href={`tel:${telefonClean}`} className="hover:text-[#98866C] whitespace-nowrap cursor-pointer font-medium">
                    {telefon}
                  </a>
                </p>
                <p className="flex items-center gap-3">
                  <i className="fa-solid fa-envelope text-[#D8ADA0]" aria-hidden="true" />
                  <a href={`mailto:${email}`} className="hover:text-[#98866C] whitespace-nowrap cursor-pointer font-medium">
                    {email}
                  </a>
                </p>
                <p className="flex items-center gap-3 text-xs text-[#5C524A]">
                  <i className="fa-solid fa-id-card text-[#D8ADA0]" aria-hidden="true" />
                  <span>CVR: {cvr}</span>
                </p>
              </div>
            </div>

            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
